import { useState } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import { Stack, Text, Paper, Switch, SegmentedControl, Group } from '@mantine/core'
import { IconAccessible, IconVolume } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { isProvisionalAtom, authUserAtom } from '../../atoms/authAtoms'
import { speechSettingsAtom, activeSpeechStoryIdAtom } from '../../atoms/speechAtoms'
import { UpgradeForm } from './UpgradeForm'
import { authService } from '../../services/authService'

// The accessibility / display preferences (and provisional-account upgrade),
// extracted from the Settings page so it can render both on the standalone
// /settings route and inside the story player as a modal — opening settings
// mid-story must NOT unmount the player.
export function SettingsPanel() {
  const isProvisional = useAtomValue(isProvisionalAtom)
  const [user, setUser] = useAtom(authUserAtom)
  const [saving, setSaving] = useState(false)
  const [speech, setSpeech] = useAtom(speechSettingsAtom)
  const activeStoryId = useAtomValue(activeSpeechStoryIdAtom)

  // Per-story override value for the active story: 'global' | 'on' | 'off'.
  const storyOverride = activeStoryId ? (speech.perStoryAutoPlay?.[activeStoryId] || 'global') : null
  const setStoryOverride = (value) => {
    setSpeech((s) => {
      const next = { ...(s.perStoryAutoPlay || {}) }
      if (value === 'global') delete next[activeStoryId]
      else next[activeStoryId] = value
      return { ...s, perStoryAutoPlay: next }
    })
  }

  const a11y = user?.a11y || {}

  // Persist a single pref. Optimistically updates the user atom first so the
  // theme resolver reskins instantly, then saves; on failure, reverts.
  const updatePref = async (key, value) => {
    if (!user) return
    const prev = user
    const optimistic = { ...user, a11y: { ...a11y, [key]: value } }
    setUser(optimistic)
    setSaving(true)
    try {
      const updated = await authService.updateAccessibility({ [key]: value })
      setUser(updated)
    } catch {
      setUser(prev)
      notifications.show({
        color: 'red',
        title: 'Could not save setting',
        message: 'Your change was not saved. Please try again.',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Stack gap="lg">
      <Paper p="xl" className="glass">
        <Group gap="xs" mb="md">
          <IconAccessible size={22} color="var(--mantine-color-cyan-5)" />
          <Text fw={600} size="lg" c="cyan.3" ff="var(--bw-heading-font)">
            Accessibility & Display
          </Text>
        </Group>

        <Stack gap="lg">
          <Switch
            label="High-contrast mode"
            description="Maximum contrast for low-vision reading. Replaces the story's theme."
            color="cyan"
            checked={!!a11y.high_contrast}
            disabled={saving}
            onChange={(e) => updatePref('high_contrast', e.currentTarget.checked)}
          />
          <Switch
            label="Reduce motion"
            description="Turns off the glow, scanline, and flicker animations."
            color="cyan"
            checked={!!a11y.reduced_motion}
            disabled={saving}
            onChange={(e) => updatePref('reduced_motion', e.currentTarget.checked)}
          />
          <Switch
            label="Dyslexia-friendly font"
            description="Switches body and headings to a high-legibility typeface."
            color="cyan"
            checked={!!a11y.dyslexia_font}
            disabled={saving}
            onChange={(e) => updatePref('dyslexia_font', e.currentTarget.checked)}
          />
          <div>
            <Text size="sm" fw={500} mb={4}>
              Minimum text size
            </Text>
            <Text size="xs" c="dimmed" mb="xs">
              Sets a floor so text never goes below this, even on the Small setting.
            </Text>
            <SegmentedControl
              color="cyan"
              value={a11y.min_text_size || 'medium'}
              disabled={saving}
              onChange={(value) => updatePref('min_text_size', value)}
              data={[
                { label: 'Small', value: 'small' },
                { label: 'Medium', value: 'medium' },
                { label: 'Large', value: 'large' },
              ]}
            />
          </div>
        </Stack>
      </Paper>

      <Paper p="xl" className="glass">
        <Group gap="xs" mb="md">
          <IconVolume size={22} color="var(--mantine-color-cyan-5)" />
          <Text fw={600} size="lg" c="cyan.3" ff="var(--bw-heading-font)">
            Speech
          </Text>
        </Group>

        <Stack gap="lg">
          <Text size="sm" c="dimmed">
            Every story beat and character message has a play button you can use any
            time, voiced by AWS Polly. Auto-play below controls whether they also
            read themselves aloud as they appear.
          </Text>
          <Switch
            label="Auto-play by default"
            description="Automatically reads each beat and character response as it appears, switching between narrator and character voices. This is the default for every story."
            color="cyan"
            checked={!!speech.autoPlayDefault}
            onChange={(e) => setSpeech((s) => ({ ...s, autoPlayDefault: e.currentTarget.checked }))}
          />

          {activeStoryId && (
            <div>
              <Text size="sm" fw={500} mb={4}>
                Auto-play for this story
              </Text>
              <Text size="xs" c="dimmed" mb="xs">
                Override the default just for the story you're playing now.
              </Text>
              <SegmentedControl
                color="cyan"
                value={storyOverride}
                onChange={setStoryOverride}
                data={[
                  { label: 'Use default', value: 'global' },
                  { label: 'On', value: 'on' },
                  { label: 'Off', value: 'off' },
                ]}
              />
            </div>
          )}
        </Stack>
      </Paper>

      {isProvisional && (
        <UpgradeForm
          title="Upgrade Account"
          description="Set your name and password to secure your account and progress."
          buttonLabel="Upgrade Account"
          buttonColor="orange"
          successMessage="Account upgraded! Your progress is now secured."
        />
      )}
    </Stack>
  )
}
